import {
  Box,
  Grid,
  IconButton,
  Paper,
  Slider,
  Table,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { DraggableWindow } from "../DraggablePopup/DraggablePopup";
import {
  update,
  togglePlaying,
  calculateTimestamp,
  setTimestamp,
} from "../../redux/slices/replay";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime, Interval } from "luxon";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay30";
import ForwardIcon from "@mui/icons-material/Forward30";
import NoSsr from "@mui/material/NoSsr";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import Button from "@mui/material/Button";
import ReplayListWrapper, { FetchedPlayback } from "./ReplayList";
import { useInterval } from "../../hooks/useInterval";
import useSWR from "swr";
import { GridOff, GridOn, Stop } from "@mui/icons-material";
import axios from "axios";
import { CreateReplay } from "./CreateReplay";
import { clear, clearArray } from "../Map/Marker/trackerSlice";
import { clear as clearWindows } from "../../trackerWindowsSlice";
import Alarms = DBSchema.Alarms;
import {
  getFormattedDateTime,
  getHourCycle,
  getDateOrderForTimezone,
  getDateFormatString,
} from "../../utils/dateTimeUtils";
import { Config } from "../../config/config";
import store from "../../reduxStore";

// Define TimeProps interface
interface TimeProps {
  interval: Interval;
  timestamp: DateTime;
  setTimestamp: (timestamp: number) => any;
  running: boolean;
  toggleRunning: Function;
  timezone: string;
}

// Define ReplayInfo interface
export interface ReplayInfo {
  start: Date;
  end: Date;
  counts: Counts;
  timestamps: { alarms: Partial<Alarms[]> };
  timezone?: string;
}

export interface Counts {
  Alarms: string;
  "Booking Changes": string;
  "Barrier Requests": string;
  "LTE Log": string;
  "Track Log": string;
}

// Interface for ControlsContainerProps
interface ControlsContainerProps extends TimeProps {
  timezone: string;
  doExitReplay: (boolean) => void;
  replayInfo?: ReplayInfo;
}

// Interface for ReplayControlsProps
interface ReplayControlsProps extends TimeProps {
  doExitReplay: (boolean) => void;
  replayInfo?: ReplayInfo;
}

// Interface for ReplayTimelineProps
interface ReplayTimelineProps extends TimeProps {
  replayInfo?: ReplayInfo;
}

export default function ControlWindow(props: {
  open: boolean;
  defaultReplays: { replays: FetchedPlayback[] };
  onLoad?: () => void;
  onEnd?: () => void;
  timezone: string;
  config: Config;
}) {
  const dispatch = useAppDispatch();
  const replaySettings = useAppSelector((state) => state.replay);

  // console.log("ControlWindow rendered with props:", {
  //   open: props.open,
  //   isReplay: replaySettings.isReplay,
  //   controlsOpen: replaySettings.controlsOpen,
  // });

  const loading = replaySettings.loading;
  const replayName = replaySettings.name;
  const startDate = DateTime.fromMillis(replaySettings.startDate);
  const endDate = DateTime.fromMillis(replaySettings.endDate);
  const { onLoad, onEnd, timezone, config } = props;

  const [view, setView] = useState(
    replaySettings.isReplay ? "controls" : "list"
  );
  // console.log(
  //   "Initial view state:",
  //   view,
  //   "isReplay:",
  //   replaySettings.isReplay
  // );

  useEffect(() => {
    setView((prevView) => {
      const newView =
        prevView === "create"
          ? "create"
          : replaySettings.isReplay
            ? "controls"
            : "list";
      // console.log(
      //   "Updating view from",
      //   prevView,
      //   "to",
      //   newView,
      //   "isReplay:",
      //   replaySettings.isReplay
      // );
      return newView;
    });
  }, [replaySettings.isReplay]);

  // When controls are reopened for an active replay, make sure we show controls view
  useEffect(() => {
    if (props.open && replaySettings.isReplay) {
      setView("controls");
    } else if (!replaySettings.isReplay) {
      // If we're no longer in replay mode (e.g., after deletion),
      // go back to the list view
      setView("list");
    }
  }, [props.open, replaySettings.isReplay]);

  // Also ensure that props.open updates work properly
  useEffect(() => {
    // console.log(
    //   "props.open changed to:",
    //   props.open,
    //   "isReplay:",
    //   replaySettings.isReplay
    // );
    if (props.open && replaySettings.isReplay && view !== "controls") {
      // console.log("Setting view to controls based on props.open change");
      setView("controls");
    }
  }, [props.open]);

  // When the window opens and replay is active, make sure we show the controls view
  useEffect(() => {
    if (props.open && replaySettings.isReplay) {
      setView("controls");
    }
  }, [props.open, replaySettings.isReplay]);

  const doExitReplay = (controlsOpen?: boolean) => {
    // console.log("doExitReplay called, controlsOpen:", controlsOpen);

    // This function should be the ONLY place in the code that actually ends a replay
    axios
      .post("/api/replay/sendCommand", {
        command: "stop",
        forceStop: true, // Add this to signal a full stop, not just a temporary one
      })
      .then((res) => {
        console.log("Replay stop command successful");

        // First clear trackers and windows BEFORE changing replay state
        dispatch(clearWindows());
        dispatch(clear());

        // Update Redux state to reflect that replay has ended - add isFromRemote: false to prevent overrides
        dispatch(
          update({
            running: false,
            isReplay: false,
            controlsOpen: controlsOpen ?? false,
            isFromRemote: false,
            clientID: undefined, // Force clientID to be regenerated
            forceStop: true, // Ensure the forceStop flag is propagated to the Redux store
          })
        );

        // Always call onEnd when exiting replay, but ONLY from this function
        if (onEnd) {
          // console.log(
          //   "Calling onEnd after successful stop command - this is the correct place"
          // );
          onEnd();
        }
      })
      .catch((error) => {
        console.error("Error stopping replay:", error);
      });
  };

  const closeWindow = useCallback(() => {
    if (replaySettings.isReplay) {
      // When in active replay, ONLY toggle the window visibility
      // Leave all other state intact - don't end the replay
      dispatch(update({ controlsOpen: false }));
    } else {
      // Only when NOT in replay mode (list/create view)
      dispatch(update({ controlsOpen: false }));
    }
  }, [replaySettings, dispatch]);

  const windowTitle = useMemo(() => {
    switch (view) {
      case "controls":
        return "Replay Controls";
      case "create":
        return "Create Replay";
      default:
        return "Replay Menu";
    }
  }, [view]);

  return (
    <DraggableWindow
      data-class="replayWindow"
      unsetBottom={true}
      center={true}
      open={props.open}
      onClose={() => closeWindow()}
      title={windowTitle}
      bounds={"parent"}
      hidePopup={view === "list" || !replaySettings.isReplay}
    >
      <Box>
        {view === "list" ? (
          <ReplayListWrapper
            defaultReplays={props.defaultReplays}
            openControls={() => setView("controls")}
            timezone={timezone}
          />
        ) : view === "create" ? (
          <CreateReplay doClose={() => setView("list")} config={config} />
        ) : (
          <ReplayContainer
            onLoad={onLoad}
            replayName={replaySettings.name}
            startDate={startDate}
            endDate={endDate}
            doExitReplay={doExitReplay}
            timezone={timezone}
          />
        )}
        {view === "list" && (
          <Box sx={{ p: 1, borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}>
            <Grid container justifyContent="center">
              <Grid item>
                <Button variant={"contained"} onClick={() => setView("create")}>
                  Create Replay
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </DraggableWindow>
  );
}

interface ReplayContainerProps {
  startDate: DateTime;
  endDate: DateTime;
  replayName: string;
  doExitReplay: (boolean) => void;
  onLoad?: () => void;
  timezone: string;
}

export function ReplayContainer(props: ReplayContainerProps) {
  const { startDate, endDate, replayName, doExitReplay, timezone } = props;
  const replaySettings = useAppSelector((state) => state.replay);
  // Use the redux state timezone if available, otherwise fall back to passed timezone
  const replayTimezone = replaySettings.timezone || timezone;
  const interval = Interval.fromDateTimes(startDate, endDate);

  const [timestamp, setLocalTimestamp] = useState(
    calculateTimestamp(replaySettings)
  );
  const [showDetail, setShowDetail] = useState(false);

  const getTimestamp = useCallback(
    () => calculateTimestamp(replaySettings),
    [replaySettings]
  );

  const running = replaySettings.running;
  const firstLoad = replaySettings.firstLoad;
  const dispatch = useAppDispatch();

  const doSendRunning = useCallback(
    (newState: boolean): Promise<any> => {
      // Still mark first load as complete
      dispatch(update({ firstLoad: false }));

      // Call sendRunning with the new desired state
      const timestamp = calculateTimestamp(replaySettings);
      return sendRunning(newState, false, timestamp);
    },
    [running, replaySettings, dispatch]
  );

  useEffect(() => {
    if (!!props.onLoad && firstLoad) {
      props.onLoad();
      // If we need initial loading, use resume instead of start
      if (running) {
        doSendRunning(true)
          .then((res) => {
            if (typeof res.running === "boolean") {
              dispatch(togglePlaying(res.running));
            }
          })
          .catch(console.error);
      }
    }
  }, [firstLoad, running, dispatch, props, doSendRunning]);

  useInterval(() => setLocalTimestamp(getTimestamp()), 10);

  useEffect(() => {
    // console.log(timestamp.toMillis(), getTimestamp().toMillis())
    // if(timestamp.toMillis() !== getTimestamp().toMillis()) {
    //     console.log("updating time")
    //     dispatch(update({timestamp: timestamp.toISO({includeOffset: false})}))
    // }
  }, [timestamp]);

  const doSetTimestamp = useCallback(
    (newTimestamp: number) => {
      dispatch(setTimestamp(newTimestamp));
      // if(running) {
      sendTimestamp(DateTime.fromMillis(newTimestamp), true).catch(
        console.error
      );
      // }
    },
    [running]
  );

  const doToggleRunning = useCallback(
    (newState: boolean) => {
      doSendRunning(newState)
        .then((res) => {
          // If we get a valid boolean response, update the state
          if (typeof res.running === "boolean") {
            dispatch(togglePlaying(res.running));
          }
        })
        .catch(console.error);
    },
    [doSendRunning, dispatch]
  );

  // useEffect(() => {
  //     sendRunning(replaySettings.running).then(res => {
  //         if(res?.running != running) {
  //             dispatch(update({running: res.running ?? running}))
  //         }
  //     }).catch(console.error)
  //     sendTimestamp(getTimestamp()).catch(console.error)
  // },[running])

  useEffect(() => {
    // console.log(replaySettings.currentIMEIs);
    dispatch(clearArray(replaySettings.currentIMEIs));
  }, [replaySettings.currentIMEIs]);

  // useEffect(() => {
  //     const send = DateTime.fromMillis(replaySettings.lastTimestamp)
  //     sendTimestamp(send).catch(console.error)
  // },[replaySettings.lastTimestamp])

  // console.log(timestamp, interval.start)
  const { progress } = calculateProgress(
    interval,
    timestamp.toMillis() - interval.start.toMillis()
  );

  //@ts-ignore
  const fetcher = (...args) => fetch(...args).then((res) => res.json());

  const { data: replayInfo } = useSWR<ReplayInfo>(
    `/api/replay/info?start=${startDate.toISO()}&end=${endDate.toISO()}`,
    fetcher
  );

  const dateOrder = getDateOrderForTimezone(
    replayInfo?.timezone || replayTimezone
  );
  const dateFormat = getDateFormatString(dateOrder);

  return (
    <NoSsr>
      <Grid container justifyContent={"center"}>
        <Paper sx={{ padding: 1, minWidth: "99%", margin: 1 }}>
          <Table size={"small"}>
            {!showDetail && (
              <TableRow onClick={() => setShowDetail(true)}>
                <TableCell>
                  <Typography fontWeight={"bold"}>{replayName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>
                    {interval.start.day !== interval.end.day ? (
                      // Different days - show both full dates
                      <>
                        {interval.start
                          .setZone(replayInfo?.timezone || replayTimezone)
                          .toFormat(dateFormat + " HH:mm:ss")}{" "}
                        to{" "}
                        {interval.end
                          .setZone(replayInfo?.timezone || replayTimezone)
                          .toFormat(dateFormat + " HH:mm:ss")}
                      </>
                    ) : (
                      // Same day - show date once and just time for end
                      <>
                        {interval.start
                          .setZone(replayInfo?.timezone || replayTimezone)
                          .toFormat(dateFormat)}{" "}
                        {interval.start
                          .setZone(replayInfo?.timezone || replayTimezone)
                          .toFormat("HH:mm:ss")}{" "}
                        to{" "}
                        {interval.end
                          .setZone(replayInfo?.timezone || replayTimezone)
                          .toFormat("HH:mm:ss")}
                      </>
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {showDetail && (
              <>
                <TableRow onClick={() => setShowDetail(false)}>
                  <TableCell>
                    <Typography fontWeight={"bold"}>Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{replayName}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={"bold"}>Start Time</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {interval.start
                        .setZone(replayInfo?.timezone || replayTimezone)
                        .toFormat(dateFormat + " HH:mm:ss")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={"bold"}>End Time</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {interval.end
                        .setZone(replayInfo?.timezone || replayTimezone)
                        .toFormat(dateFormat + " HH:mm:ss")}
                    </Typography>
                  </TableCell>
                </TableRow>
                {Object.entries(replayInfo?.timestamps ?? {}).map((entry) => (
                  <ReplayInfoRow key={entry[0] + "-info-row"} entry={entry} />
                ))}
              </>
            )}
            <TableRow>
              <TableCell>
                <Typography>Timestamp</Typography>
              </TableCell>
              <TableCell>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Typography>
                    {timestamp
                      .setZone(replayInfo?.timezone || replayTimezone)
                      .toFormat(dateFormat + " HH:mm:ss")}
                  </Typography>
                  <Typography>{progress.toFixed(0)}%</Typography>
                </Box>
              </TableCell>
            </TableRow>
          </Table>

          <ControlsContainer
            replayInfo={replayInfo}
            interval={interval}
            timestamp={timestamp}
            setTimestamp={doSetTimestamp}
            running={running}
            toggleRunning={doToggleRunning}
            doExitReplay={doExitReplay}
            timezone={replayInfo?.timezone || replayTimezone}
          />
        </Paper>
      </Grid>
    </NoSsr>
  );
}

export function ReplayInfoRow(props: { entry: any }) {
  const { entry } = props;
  const [showTimestamps, setShowTimestamps] = useState(false);
  const dateOrder = getDateOrderForTimezone(entry[0]);
  const dateFormat = getDateFormatString(dateOrder);
  const formattedDate = DateTime.fromISO(entry[0])
    .setZone(entry[0])
    .toFormat(dateFormat);
  return (
    <>
      <TableRow
        onClick={() => setShowTimestamps((old) => !old)}
        key={entry[0] + "-count"}
      >
        <TableCell>{`${formattedDate}`}</TableCell>
        <TableCell>{`${entry[1].length}`}</TableCell>
      </TableRow>
      {showTimestamps &&
        entry[1].map((alarm, index) => (
          <TableRow key={entry[0] + "-item-" + index}>
            <TableCell>{alarm?.vtuid ?? alarm?.imei}</TableCell>
            <TableCell>
              {alarm?.time
                ? DateTime.fromISO(alarm.time)
                    .setZone(entry[0])
                    .toFormat(dateFormat + " HH:mm:ss")
                : ""}
            </TableCell>
            <TableCell>{alarm?.alert!}</TableCell>
          </TableRow>
        ))}
    </>
  );
}

// function usesAmpm(timezone: string): boolean {
//   const ampmPrefixes = [
//     "America/",
//     "Australia/",
//     "Pacific/Honolulu",
//     "Pacific/Auckland",
//     "Asia/Manila",
//     "Asia/Kolkata",
//   ];
//   return ampmPrefixes.some((prefix) => timezone.startsWith(prefix));
// }

// function getFormattedDateTime(time: DateTime, timezone: string) {
//   const t = time.setZone(timezone);
//   // Use "h12" for AM/PM and "h23" for 24-hour format:
//   const hourCycle = usesAmpm(timezone) ? "h12" : "h23";
//   return (
//     t.toLocaleString({ dateStyle: "short" }) +
//     ", " +
//     t.toLocaleString({ timeStyle: "short", hourCycle }) +
//     " " +
//     timezone
//   );
// }

function ControlsContainer(props: ControlsContainerProps) {
  // const [success, setSuccess] = useState(false)

  // useEffect(() => {
  //     const period = 100
  //     // sendRunning(running, )
  //     if(running) {
  //         // sendTimestamp(timestamp).then(console.log).catch(console.error)
  //         const timer = setInterval(() => {
  //             setTimestamp((prevTimestamp) => {
  //                 const newTimestamp = prevTimestamp.plus({millisecond: period})
  //                 if(!interval.contains(newTimestamp)) {
  //                     toggleRunning(false)
  //                     return interval.end
  //                 }
  //                 return newTimestamp
  //             })
  //         }, period)
  //         return () => clearInterval(timer)
  //     }
  // },[running])

  return (
    <Grid
      container
      justifyContent={"center"}
      alignItems={"center"}
      flexDirection={"column"}
    >
      <Grid item>
        <ReplayTimeline {...props} />
      </Grid>
      <Grid item>
        <ReplayControls {...props} />
      </Grid>
    </Grid>
  );
}

interface ReplayControlsProps extends TimeProps {
  doExitReplay: (boolean) => void;
  replayInfo?: ReplayInfo;
}

export function ReplayControls({
  replayInfo,
  interval,
  timestamp,
  setTimestamp,
  running,
  toggleRunning,
  doExitReplay,
  timezone,
}: ReplayControlsProps) {
  const replaySettings = useAppSelector((state) => state.replay);
  const isLoading = replaySettings.loading || false;

  const addToTimestamp = useCallback(
    (millis: number) => {
      console.log("adding " + millis);
      let newTimestamp = timestamp.plus({ millisecond: millis });
      if (interval.start > newTimestamp) {
        newTimestamp = interval.start;
      }
      if (interval.end < newTimestamp) {
        newTimestamp = interval.end;
      }
      sendJump(millis).catch(console.error);
      // setTimestamp(newTimestamp.toMillis())
      // sendTimestamp(newTimestamp).catch(console.error)
      // if(running) {
      //     sendTimestamp(newTimestamp).catch(console.error)
      // }
    },
    [timestamp, interval]
  );

  return (
    <Grid container justifyContent={"center"} alignItems={"center"}>
      <IconButton
        onClick={() => setTimestamp(interval.start.toMillis())}
        disabled={isLoading}
      >
        <FirstPageIcon
          fontSize={"large"}
          color={isLoading ? "inherit" : "primary"}
        />
      </IconButton>
      {/* <IconButton onClick={() => addToTimestamp(-30000)}>
          <ReplayIcon fontSize={"large"} color={"primary"} />
        </IconButton> */}
      <IconButton
        color={running ? "error" : "primary"}
        onClick={() => toggleRunning(!running)}
        disabled={isLoading}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : running ? (
          <PauseIcon fontSize={"large"} />
        ) : (
          <PlayArrowIcon fontSize={"large"} />
        )}
      </IconButton>
      {/* <IconButton onClick={() => addToTimestamp(30000)}>
          <ForwardIcon fontSize={"large"} color={"primary"} />
        </IconButton> */}
      <IconButton
        color={"error"}
        onClick={() => doExitReplay(true)}
        disabled={isLoading}
      >
        <Stop fontSize={"large"} />
      </IconButton>
    </Grid>
  );
}

interface ReplayTimelineProps extends TimeProps {
  replayInfo?: ReplayInfo;
}

function calculateProgress(interval: Interval, value: number) {
  const date = interval.start
    .plus({ millisecond: value })
    .set({ millisecond: 0 })
    .toISO({
      includeOffset: false,
      suppressMilliseconds: true,
    });
  const duration = interval.toDuration().toMillis();
  const progress = (value / duration) * 100;
  return { date, progress };
}

function ReplayTimeline(props: ReplayTimelineProps) {
  const { timestamp, setTimestamp, interval, running, timezone } = props;
  const dateOrder = getDateOrderForTimezone(timezone);
  const dateFormat = getDateFormatString(dateOrder);
  const diff = timestamp.diff(interval.start);

  // --- Get loading state ---
  const replaySettings = useAppSelector((state) => state.replay);
  const isLoading = replaySettings.loading || false;
  // --- Log loading state changes ---
  // useEffect(() => {
  //   console.log(`[ReplayTimeline] isLoading state changed to: ${isLoading}`);
  // }, [isLoading]);
  // -------------------------------

  const changeTimestamp = (value: number | number[], commit: boolean) => {
    if (running) {
      console.log("replay is running, refusing to change timestamp");
      return;
    }
    if (typeof value !== "number") {
      console.error(
        "got unexpected type for timestamp value",
        value,
        typeof value
      );
      return;
    }
    const newTimestamp = interval.start.plus({ millisecond: value });
    const newValue = newTimestamp.toMillis();
    if (commit) {
      // console.log("change committed");
      setTimestamp(newValue);
      // console.log(newTimestamp)
      // sendTimestamp(newTimestamp).then(console.log).catch(console.error)
    }
    // console.log("changing local timestamp")
    setSliderValue(value);
  };

  const showDateInMarks = interval.start.day !== interval.end.day;
  const getMarkLabel = (time: DateTime, showDate: boolean) =>
    showDate
      ? time.setZone(timezone).toFormat(dateFormat)
      : time.setZone(timezone).toFormat("HH:mm:ss");

  const duration = interval.toDuration().toMillis();

  const eventMarks: Array<{ value: number; label: string; name?: string }> =
    useMemo(() => {
      return (
        props.replayInfo?.timestamps?.alarms.map((alarm) => {
          //@ts-ignore
          const time = DateTime.fromISO(alarm.time);
          const diff = time.diff(interval.start).toMillis();
          return {
            value: diff,
            label: "",
            name: `${alarm?.vtuid ?? alarm?.imei} - ${alarm?.alert}`,
          };
        }) ?? []
      );
    }, [
      interval.start,
      interval.end,
      props.replayInfo?.timestamps?.alarms.length,
    ]);

  const marks = [
    {
      value: 0,
      label: getMarkLabel(interval.start, showDateInMarks),
    },
    ...eventMarks,
    {
      value: duration,
      label: getMarkLabel(interval.end, showDateInMarks),
    },
  ];

  const [sliderValue, setSliderValue] = useState(diff.milliseconds);
  useEffect(() => {
    setSliderValue(diff.milliseconds);
  }, [diff.milliseconds]);

  // by having the color in state, we load with 'error' and replace it with 'primary' after the first render
  // this lets us load the 'error' style before the user opens the popup. if the popup opens before the 'error'
  // style has been loaded, it causes issues
  const [color, setColor] = useState("error");
  useEffect(() => {
    setColor(running ? "error" : "primary");
  }, [running]);

  const [step, setStep] = useState<number | null>(1);

  return (
    <Grid container spacing={3} justifyItems={"center"} sx={{ width: 300 }}>
      <Grid item xs={1}></Grid>
      <Grid item xs={10}>
        <Slider
          value={sliderValue}
          min={0}
          max={interval.toDuration().toMillis()}
          onChange={(event, value) => changeTimestamp(value, false)}
          onChangeCommitted={(event, value) => changeTimestamp(value, true)}
          {
            //@ts-ignore
            ...{}
          }
          //@ts-ignore
          color={color}
          step={step}
          getAriaValueText={(value) =>
            interval.start
              .plus({ millisecond: value })
              .setZone(timezone)
              .toFormat(dateFormat + " HH:mm:ss") +
            " " +
            timezone
          }
          valueLabelFormat={(value) => {
            let markItem = <></>;
            const markIndex = eventMarks.findIndex(
              (mark) => mark.value === value
            );
            if (markIndex >= 0) {
              const name = eventMarks[markIndex].name;
              if (name) {
                markItem = <Typography variant="body2">{name}</Typography>;
              }
            }
            const dt = interval.start
              .plus({ millisecond: value })
              .setZone(timezone);

            // Use dateFormat with time
            const dateTimeLabel = dt.toFormat(dateFormat + " HH:mm:ss");

            return (
              <>
                {markItem}
                <Typography variant="body2">{dateTimeLabel}</Typography>
              </>
            );
          }}
          valueLabelDisplay={"auto"}
          marks={marks}
          disabled={isLoading}
        />
      </Grid>
      <Grid item xs={1}>
        <Tooltip
          title={step ? "Enable event snapping" : "Disable event snapping"}
        >
          <IconButton
            onClick={() => setStep((oldStep) => (oldStep === null ? 1 : null))}
          >
            {step ? <GridOn /> : <GridOff />}
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
}

async function sendTimestamp(timestamp: DateTime, includeOffset = false) {
  // Immediately dispatch loading state BEFORE sending the command
  store.dispatch(
    update({
      loading: true,
      message: "Repositioning replay...", // Or simply 'Loading...'
    })
  );

  try {
    const res = await axios.post("/api/replay/sendCommand", {
      command: "setTimestamp",
      timestamp: timestamp.toUTC().toMillis(),
    });
    // The backend will eventually send a replay-response with loading: false
    // which will be handled by the websocket listener to turn off the spinner.
    return res.data;
  } catch (error) {
    console.error("Error sending setTimestamp command:", error);
    // Optionally dispatch an error state here
    store.dispatch(
      update({
        loading: false, // Turn off loading on error
        error: "Failed to set timestamp",
      })
    );
    throw error; // Re-throw if needed
  }
}

async function sendJump(diffms: number) {
  const res = await axios.post("/api/replay/sendCommand", {
    command: "jump",
    diff: `${diffms}ms`,
  });
  return res.data;
}

async function sendRunning(
  running: boolean,
  firstLoad?: boolean,
  timestamp?: DateTime
) {
  let timestampObject = timestamp ? { timestamp: timestamp.toMillis() } : {};
  const res = await axios.post("/api/replay/sendCommand", {
    ...timestampObject,
    command: running ? "resume" : "pause",
  });

  return res.data;
}
